import { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import dayjsTZ from "./dayjs";
import {
  SEASONAL_THEMES_FIELD_ID,
  THEME_PRIORITY,
  THEME_LABELS,
  buildDefaultSeasonalThemesOptions,
} from "../constantsFactory/seasonalThemes";

/**
 * Normalize / fill missing keys on a stored fieldOptions blob.
 */
export function normalizeSeasonalThemesOptions(raw) {
  const defaults = buildDefaultSeasonalThemesOptions();
  if (!raw || typeof raw !== "object") return defaults;

  const byId = new Map(
    (Array.isArray(raw.themes) ? raw.themes : []).map((t) => [t.id, t]),
  );

  const themes = defaults.themes.map((def) => {
    const saved = byId.get(def.id);
    if (!saved) return def;
    return {
      ...def,
      ...saved,
      id: def.id,
      label: saved.label || def.label,
      effects: {
        ...def.effects,
        ...(saved.effects || {}),
      },
    };
  });

  return {
    enabled: !!raw.enabled,
    timezone: raw.timezone || defaults.timezone,
    forceThemeId: raw.forceThemeId || null,
    themes,
  };
}

/**
 * Resolve the single active seasonal theme for "now".
 * @returns {{ id: string, label: string, effects: object } | null}
 */
export function resolveActiveSeasonalTheme(fieldOptions, now = dayjsTZ()) {
  const opts = normalizeSeasonalThemesOptions(fieldOptions);
  if (!opts.enabled) return null;

  const themesById = new Map(opts.themes.map((t) => [t.id, t]));

  if (opts.forceThemeId && themesById.has(opts.forceThemeId)) {
    const t = themesById.get(opts.forceThemeId);
    return {
      id: t.id,
      label: t.label || THEME_LABELS[t.id] || t.id,
      effects: t.effects || {},
      forced: true,
    };
  }

  const today = now.format("YYYY-MM-DD");
  const matches = opts.themes.filter((t) => {
    if (!t?.startDate || !t?.endDate) return false;
    return t.startDate <= today && today <= t.endDate;
  });

  if (!matches.length) return null;

  matches.sort(
    (a, b) => (THEME_PRIORITY[b.id] || 0) - (THEME_PRIORITY[a.id] || 0),
  );
  const winner = matches[0];
  return {
    id: winner.id,
    label: winner.label || THEME_LABELS[winner.id] || winner.id,
    effects: winner.effects || {},
    forced: false,
  };
}

export function findSeasonalThemesField(programFieldsList) {
  if (!Array.isArray(programFieldsList)) return null;
  return (
    programFieldsList.find((f) => f?.fieldId === SEASONAL_THEMES_FIELD_ID) ||
    programFieldsList.find(
      (f) =>
        f?.fieldName === "Seasonal Themes" ||
        f?.fieldName === "Tema sezonale",
    ) ||
    null
  );
}

/**
 * Hook: active theme from Redux programFields + sync documentElement dataset.
 */
export function useSeasonalTheme() {
  const programFieldsList = useSelector(
    (state) => state.programFields?.programFields,
  );

  const field = useMemo(
    () => findSeasonalThemesField(programFieldsList),
    [programFieldsList],
  );

  const options = useMemo(
    () => normalizeSeasonalThemesOptions(field?.fieldOptions),
    [field],
  );

  const active = useMemo(
    () => resolveActiveSeasonalTheme(options),
    [options],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (active?.id) {
      root.dataset.seasonalTheme = active.id;
    } else {
      delete root.dataset.seasonalTheme;
    }
    return () => {
      delete root.dataset.seasonalTheme;
    };
  }, [active?.id]);

  return { active, options, field };
}
